import React, { useState, useEffect, useRef } from 'react';
import '../styles/Contact.scss';
import emailjs from '@emailjs/browser';
import ReCAPTCHA from 'react-google-recaptcha';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    sent_date: new Date().toLocaleString()
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const contactRef = useRef(null);
  const recaptchaRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize EmailJS with your User ID
  useEffect(() => {
    emailjs.init("T108CbfCYACZzQdAi"); // Replace with your actual EmailJS User ID
  }, []);
  
  // Monitor formSubmitted state and handle the success message display
  useEffect(() => {
    if (formSubmitted) {      
      // Hide success message after 5 seconds to ensure it's visible
      const timer = setTimeout(() => {
        setFormSubmitted(false);
      }, 5000);
      
      // Clean up timer if component unmounts
      return () => clearTimeout(timer);
    }
  }, [formSubmitted]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!captchaVerified) {
      setError('Please verify that you are not a robot.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    // Prepare template parameters
    const templateParams = {
      from_name: formData.name,
      reply_to: formData.email,
      subject: formData.subject,
      message: formData.message,
      sent_date: formData.sent_date
    };
    
    // Send email using EmailJS
    emailjs.send(
      'service_xblhj7k', // Replace with your EmailJS service ID
      'template_iun1hch', // Replace with your EmailJS template ID
      templateParams
    )
      .then(() => {
        console.log('Email sent successfully!');
        setFormSubmitted(true);
        setLoading(false);
        
        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          sent_date: new Date().toLocaleString()
        });
        
        // Reset reCAPTCHA
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
          setCaptchaVerified(false);
        }
      })
      .catch((err) => {
        console.error('Failed to send email:', err);
        setError('Failed to send message. Please try again later.');
        setLoading(false);
      });
  };

  // Setup intersection observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  return (
    <section className="contact-section" ref={contactRef}>
      <h2 className="animate-on-scroll">Get In Touch</h2>
      
      <div className="contact-content">
        <div className="contact-form animate-on-scroll">
          {formSubmitted ? (
            <div className="success-message">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p><strong>Thank you for your message!</strong> I'll get back to you soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <div className="error-message">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p>{error}</p>
                </div>
              )}
            
              <div className="form-group">
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name} 
                  onChange={handleChange} 
                  required 
                  placeholder=" "
                  disabled={loading}
                />
                <label htmlFor="name">Your Name</label>
              </div>
              
              <div className="form-group">
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleChange} 
                  required 
                  placeholder=" "
                  disabled={loading}
                />
                <label htmlFor="email">Your Email</label>
              </div>
              
              <div className="form-group">
                <input 
                  type="text" 
                  id="subject" 
                  name="subject" 
                  value={formData.subject} 
                  onChange={handleChange} 
                  required 
                  placeholder=" "
                  disabled={loading}
                />
                <label htmlFor="subject">Subject</label>
              </div>
              
              <div className="form-group">
                <textarea 
                  id="message" 
                  name="message" 
                  value={formData.message} 
                  onChange={handleChange} 
                  required 
                  placeholder=" "
                  disabled={loading}
                ></textarea>
                <label htmlFor="message">Your Message</label>
              </div>
              
              <div className="recaptcha-container">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey="6LcQkjgrAAAAAEakG9qVFoIyOIcwtqoN_yvC6Sx0"
                  onChange={(value) => {
                    setCaptchaVerified(!!value);
                    if (value) setError(null);
                  }}
                  onExpired={() => setCaptchaVerified(false)}
                  disabled={loading}
                />
              </div>
              
              <button type="submit" disabled={loading || !captchaVerified}>
                {loading ? (
                  <span className="loading-spinner">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="animate-spin">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Sending...
                  </span>
                ) : 'Send Message'}
              </button>
            </form>
          )}
        </div>
        
        <div className="contact-info">
          <div className="info-card animate-on-scroll">
            <h3>Contact Information</h3>
            
            <div className="info-item staggered-animation animate-on-scroll">
              <div className="icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="content">
                <h4>Email</h4>
                <a href="mailto:phat.lethinh@outlook.com.vn">phat.lethinh@outlook.com.vn</a>
              </div>
            </div>
            
            <div className="info-item staggered-animation animate-on-scroll">
              <div className="icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div className="content">
                <h4>Phone</h4>
                <p>+84 (0) 34 253 4854</p>
              </div>
            </div>
            
            <div className="info-item staggered-animation animate-on-scroll">
              <div className="icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="content">
                <h4>Location</h4>
                <p>Ho Chi Minh City, Vietnam</p>
              </div>
            </div>
            
            <div className="social-links">
              <a href="https://github.com/AntonyLe23" target="_blank" rel="noopener noreferrer">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
                </svg>
              </a>
              <a href="https://www.linkedin.com/in/phat-anthony-le-84ab0117a/" target="_blank" rel="noopener noreferrer">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                  <rect x="2" y="9" width="4" height="12"></rect>
                  <circle cx="4" cy="4" r="2"></circle>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
